/** Construction recipes from Frank Mantooth, Voicings for Jazz Keyboard,
 * chapters 2, 4, 6 and 9. Offsets are semitones relative to the chord root,
 * ascending across both hands. See docs/mantooth-piano-review.md for sources,
 * scope, register exceptions and the distinction from generic fourth-stacking. */
import type { ChordTypeId } from './chords';

export interface MantoothShape {
  offsets: number[];
  split: number;
  recipe: 'generic' | 'miracle' | 'polychord' | 'diminished' | 'half-diminished';
  /** Root substitution below middle C is appropriate for these constructions. */
  lowRoot?: boolean;
  triadRoot?: number;
}

const mod = (n: number) => ((n % 12) + 12) % 12;
const fourths = (top: number): MantoothShape => ({
  offsets: [-20, -15, -10, -5, 0].map((n) => n + top),
  split: 2,
  recipe: 'generic',
  lowRoot: true,
});
const miracle = (top: number): MantoothShape => ({
  offsets: [-19, -14, -9, -4, 0].map((n) => n + top),
  split: 2,
  recipe: 'miracle',
  lowRoot: true,
});

/** Chapter 6: a major triad in any inversion, over the nearest ordered LH
 * guide pair. A doubled guide may give way to the root. Suspended dominants
 * instead use root/fifth in either order and never introduce the major third. */
function polychords(triadRoot: number, suspended = false): MantoothShape[] {
  const result: MantoothShape[] = [];
  const triad = [triadRoot, triadRoot + 4, triadRoot + 7];
  const pairs = suspended
    ? [
        [0, 7],
        [7, 0],
      ]
    : [
        [4, 10],
        [10, 4],
      ];
  if (!suspended) {
    if (triad.some((n) => mod(n) === 4)) pairs.push([0, 10], [10, 0]);
    if (triad.some((n) => mod(n) === 10)) pairs.push([0, 4], [4, 0]);
  }
  for (let inversion = 0; inversion < 3; inversion += 1) {
    const right = [...triad.slice(inversion), ...triad.slice(0, inversion).map((n) => n + 12)];
    for (const pair of pairs) {
      // Place each LH pair immediately below the RH, rather than freely
      // combining octaves that can leave an empty octave between the hands.
      const leftTop = right[0] - (mod(right[0] - pair[1]) || 12);
      const leftBottom = leftTop - (mod(pair[1] - pair[0]) || 12);
      result.push({
        offsets: [leftBottom, leftTop, ...right],
        split: 2,
        recipe: 'polychord',
        lowRoot: true,
        triadRoot,
      });
    }
  }
  return result;
}

export function mantoothShapes(type: ChordTypeId): MantoothShape[] {
  switch (type) {
    case 'maj':
    case 'maj7':
    case 'maj9':
      return [fourths(0), fourths(7), miracle(4), miracle(11)];
    case 'maj6':
    case 'maj69':
      return [fourths(0), miracle(4)];
    case 'min':
    case 'min7':
    case 'min9':
    case 'min6':
    case 'min69':
      return [fourths(3), miracle(7)];
    case 'maj7b5':
      // Chapter 4: the two Lydian functions contain the written #11/b5.
      return [fourths(2), miracle(6)];
    case 'dom7':
    case 'dom9':
    case 'dom13':
      return [
        { offsets: [-20, -14, -10, -5, 0], split: 2, recipe: 'generic', lowRoot: true },
        { offsets: [-14, -8, -3, 2, 7], split: 2, recipe: 'generic', lowRoot: true },
      ];
    case 'dom7sus4':
      return [fourths(10), miracle(2), ...polychords(10, true)];
    case 'dom7b5':
      return polychords(2); // II / I7: 9, #11, 13
    case 'dom7s9':
      return polychords(3); // bIII / I: #9, 5, b7
    case 'dom7b9':
      return polychords(9); // VI / I7: 13, b9, 3
    case 'dom7s5':
      return polychords(8); // bVI / I7: #5, root, #9
    case 'dim7':
      // Chapter 9's four-note colour is deliberately not a literal dim7
      // pitch-class set. It may plane by minor thirds over the same harmony.
      return [0, 3, 6, 9].map((top) => ({
        offsets: [-15, -9, -4, 0].map((n) => n + top),
        split: 1,
        recipe: 'diminished',
      }));
    case 'min7b5':
      return [
        { offsets: [0, 6, 10, 15], split: 2, recipe: 'half-diminished' },
        // Generic dominant a major third below: its third is our root.
        { offsets: [-18, -12, -7, -2, 3], split: 2, recipe: 'half-diminished' },
      ];
    default:
      // Explicit colours absent from these recipes retain the existing
      // chord-specific fallback; do not erase an altered fifth/ninth/seventh.
      return [];
  }
}
